// The vendor-facing "you need to action this" indicators, ported from the
// sister repo's vendor portal and kept out of Questionnaire.tsx so the wizard
// only gains thin call sites. Every count shown here comes from the shared
// needsVendorAction rule (packages/web-shared/src/lib/vendorActions.ts).
import { actionReasonLabel, isReturnedReason, type VendorActionItem } from '@l2u/web-shared';

/**
 * The red count pill on a nav item. Same classes as the sister repo's
 * CompanyShell badge; renders nothing at 0 (or below), so a clean slate shows
 * no bubble at all rather than a "0".
 *
 * `inline` sits it after the label (a text tab); otherwise it is absolutely
 * positioned over the top-right corner of its (relative) parent, the icon.
 *
 * `announce={false}` renders the pill for sighted users only, for a pill that
 * sits BEFORE the label (an icon badge): pair it with <NavCountSrText> after
 * the label so a screen reader hears "Questionnaire (3 items need your
 * action)", not the count first.
 */
export function NavCountBadge({
  count,
  label = 'items need your action',
  srText,
  inline = false,
  announce = true,
}: {
  count: number;
  label?: string;
  /** Replaces "{count} {label}" in the announcement, for a non-count badge. */
  srText?: string;
  inline?: boolean;
  announce?: boolean;
}) {
  if (!(count > 0)) return null;
  const position = inline ? 'relative ml-1.5 inline-flex' : 'absolute -right-2.5 -top-1.5 flex';
  // Visually-hidden text rather than aria-label: an aria-label on a plain
  // <span> (no role) is not reliably announced, and inside a link it would not
  // join the link's accessible name. The bare digit is hidden from assistive
  // tech so it is not read twice. The pill itself is positioned, so the
  // sr-only child anchors to it and cannot widen a scrolling tab row.
  return (
    <span
      className={`${position} h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white`}
    >
      <span aria-hidden="true">{count}</span>
      {announce && (
        <NavCountSrText count={count} label={label} {...(srText ? { text: srText } : {})} />
      )}
    </span>
  );
}

/** The visually-hidden " (n items need your action)" text; nothing at 0. */
export function NavCountSrText({
  count,
  label = 'items need your action',
  text,
}: {
  count: number;
  label?: string;
  /** Replaces "{count} {label}" — e.g. "company information needs your action". */
  text?: string;
}) {
  if (!(count > 0)) return null;
  return (
    <span className="sr-only">
      {' '}
      ({text ?? `${count} ${label}`})
    </span>
  );
}

/**
 * The detail line under the "Action required" banner. Names only the controls
 * this wizard actually has: the asset wizard mounts without comment threads,
 * so telling an asset owner to reply on a thread would point at a control
 * that is not there. Pass the wizard's WizardContext flags.
 *
 * `byReply` is how many of the `count` are sent-back DOCUMENT answers
 * (answerReturnsByReply, @l2u/shared): those have no "Submit to admin" and no
 * card controls — the replacement goes in as a reply on the comment thread,
 * and sending it hands the answer back. Without a thread there is no such
 * route, so the count is ignored and every answer goes the card's way.
 */
export function actionBannerDetail(
  count: number,
  caps: { supportsAnswerResubmit: boolean; supportsCommentThreads: boolean },
  byReply = 0,
): string {
  const one = count === 1;
  const docs = caps.supportsCommentThreads ? Math.min(Math.max(byReply, 0), count) : 0;
  let text = `${count} question${one ? '' : 's'} marked in red.`;
  if (docs > 0 && docs === count) {
    // Every one is a returned document: naming "Submit to admin" or "Update"
    // would point at controls these cards do not have.
    return `${text} Reply on ${one ? 'its' : 'each one’s'} comment thread with the replacement document — sending it hands the answer back to the admin.`;
  }
  text += ` Update ${one ? 'it' : 'each one'}`;
  if (caps.supportsAnswerResubmit) {
    text += `, then send ${one ? 'it' : 'a returned answer'} back with “Submit to admin”`;
  }
  text += '.';
  if (docs > 0) {
    text +=
      ' A returned document is the exception: reply on its comment thread with the replacement, which hands it back to the admin.';
  }
  if (caps.supportsCommentThreads) text += ' Reply on the comment thread where the admin left one.';
  return text;
}

/** The QuestionCard frame: a red border when the vendor owes an action on it. */
export function questionCardFrameClass(item: VendorActionItem | null | undefined): string {
  return item ? 'border-red-400' : 'border-slate-200';
}

/**
 * Whether a question card opens its comment thread by itself, inline: the admin
 * handed the answer back (a rejection, or the last word on the thread) AND
 * there is an admin comment to read. Without this the card said "Reply to the
 * admin" while the message itself sat behind a "Comments" button. A returned
 * item with no admin thread (a bare rejection — its reviewer_comment already
 * shows in the card) has nothing to open.
 */
export function opensCommentThreadInline(
  item: VendorActionItem | null | undefined,
  hasAdminComment: boolean | undefined,
): boolean {
  return !!item && isReturnedReason(item.reason) && hasAdminComment === true;
}

/** The small "Action required · <why>" label in a flagged card's header. */
export function ActionRequiredTag({ item }: { item: VendorActionItem | null | undefined }) {
  if (!item) return null;
  return (
    <span className="mt-1 inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
      Action required · {actionReasonLabel(item.reason)}
    </span>
  );
}

/** The red count dot on a stepper circle; nothing at 0. */
export function StepActionDot({ count }: { count: number }) {
  if (!(count > 0)) return null;
  return (
    <span
      className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white ring-2 ring-white"
      aria-hidden
    >
      {count}
    </span>
  );
}

export type ActionViewMode = 'needs' | 'all';

/**
 * The post-submit summary line and the "Needs action (n)" / "All questions"
 * switch, shown above the step content once a questionnaire has been sent for
 * review. The sister repo renders these as tabs over a flat list; this wizard
 * keeps its stepper, so the switch filters the questions of the current step
 * and the stepper's red dots say which steps hold the rest.
 */
export function NeedsActionBar({
  needCount,
  total,
  mode,
  onModeChange,
}: {
  needCount: number;
  total: number;
  mode: ActionViewMode;
  onModeChange: (mode: ActionViewMode) => void;
}) {
  if (total === 0) return null;
  const tab = (key: ActionViewMode, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={mode === key}
      onClick={() => onModeChange(key)}
      className={`inline-flex items-center rounded-md px-3 py-1.5 text-sm transition ${
        mode === key
          ? 'bg-white font-semibold text-slate-900 shadow-sm'
          : 'font-medium text-slate-500 hover:text-slate-700'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
      <span className="text-sm font-medium text-slate-700">
        <span className={needCount > 0 ? 'text-red-600' : 'text-emerald-600'}>{needCount}</span> of{' '}
        {total} questions need your action
      </span>
      <div role="tablist" aria-label="Questionnaire view" className="flex gap-1 rounded-lg bg-slate-100 p-1">
        {tab('needs', `Needs action (${needCount})`)}
        {tab('all', `All questions (${total})`)}
      </div>
    </div>
  );
}
